/**
 * appointmentDisplay — single source of truth for how an appointment is
 * colored / labeled on the calendar.
 *
 * Two clinic-wide color modes (clinic.calendarColorMode):
 *   - "status"        (default, field absent) — block color reflects the
 *                     lifecycle + SMS-confirmation state (historic behavior).
 *   - "treatmentType" — block color is the treatment type's color; the
 *                     confirmation state is shown as a small text badge.
 *                     Blocked time and cancelled meetings keep their status look.
 *
 * `getAppointmentStatusInfo` was moved here verbatim from AppointmentCard so
 * status mode stays pixel-identical.
 */
import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  Check,
  Checks,
  Clock,
  X,
  SealCheck,
  ClockCountdown,
  Hourglass,
  Prohibit,
} from "@phosphor-icons/react";
import { selectClinicId, selectClinicProfile } from "../store/slices/authSlice";
import { useGetCategoriesQuery } from "../store/services/tasksApi";

export const CALENDAR_COLOR_MODES = {
  STATUS: "status",
  TREATMENT_TYPE: "treatmentType",
};

// System primary (--color-primary in variables.css) — used for meetings with
// no / unknown treatment type in type mode. Hex (not var()) because it feeds
// hexToRgba for the tint.
export const TYPE_FALLBACK_COLOR = "#4dd9b4";

export const getCalendarColorMode = (clinicProfile) =>
  clinicProfile?.calendarColorMode === CALENDAR_COLOR_MODES.TREATMENT_TYPE
    ? CALENDAR_COLOR_MODES.TREATMENT_TYPE
    : CALENDAR_COLOR_MODES.STATUS;

// ---------------------------------------------------------------------------
// Status mode (moved verbatim from AppointmentCard.js)
// ---------------------------------------------------------------------------

const isEnded = (appointment) => {
  if (appointment.status === "cancelled") return false;
  if (appointment.status === "completed") return true;
  if (!appointment.endTime) return false;
  const endMs = new Date(appointment.endTime).getTime();
  return !isNaN(endMs) && endMs < Date.now();
};

const isFullyHandled = (appointment) =>
  appointment.isApprovedByTherapist === true &&
  appointment.isTherapistSummarizedTheMeeting === true &&
  appointment.isTherapistUpdatedPaymentStatus === true;

export const getAppointmentStatusInfo = (appointment) => {
  // Blocked time — must be first: block docs never progress through the
  // ended/handled lifecycle, so no other branch may claim them.
  if (appointment.isBlockedTime === true) {
    return { color: "blocked", icon: Prohibit, label: "זמן חסום" };
  }
  // Patient self-booking awaiting therapist approval — distinct amber state.
  if (appointment.status === "pending_approval") {
    return { color: "amber", icon: Hourglass, label: "ממתין לאישור" };
  }
  if (appointment.status === "cancelled") {
    return { color: "red", icon: X, label: "בוטל" };
  }
  if (isEnded(appointment) && isFullyHandled(appointment)) {
    return { color: "blue", icon: SealCheck, label: "טופל במלואו" };
  }
  if (isEnded(appointment)) {
    return { color: "orange", icon: ClockCountdown, label: "ממתין לעדכון" };
  }
  // Any confirmation declined - show red with X
  if (
    appointment.whatsappInitialDeclined === true ||
    appointment.whatsappReminderDeclined === true
  ) {
    return { color: "red", icon: X, label: "לא אישרו" };
  }
  // Both confirmations received - turquoise with double check
  if (
    appointment.whatsappInitialConfirmed === true &&
    appointment.whatsappReminderConfirmed === true
  ) {
    return { color: "turquoise", icon: Checks, label: "אישרו פעמיים" };
  }
  // At least one confirmed (and none declined) - turquoise with single check
  if (
    appointment.whatsappInitialConfirmed === true ||
    appointment.whatsappReminderConfirmed === true
  ) {
    return { color: "turquoise", icon: Check, label: "אישור" };
  }
  // No SMS was requested - treat as confirmed (turquoise)
  if (
    appointment.initialSmsEnabled === false &&
    appointment.reminderSmsEnabled === false
  ) {
    return { color: "turquoise", icon: Check, label: "ללא SMS" };
  }
  // Fallback: no SMS activity at all (handles old data where flags may not be set)
  if (
    !appointment.whatsappInitialSent &&
    !appointment.whatsappReminderSent &&
    !appointment.initialSmsScheduledAt &&
    !appointment.reminderSmsScheduledAt
  ) {
    return { color: "turquoise", icon: Check, label: "ללא SMS" };
  }
  // All are null - waiting for response - yellow with clock
  return { color: "yellow", icon: Clock, label: "ממתין" };
};

// ---------------------------------------------------------------------------
// Confirmation state (independent of lifecycle) — same flag precedence as above
// ---------------------------------------------------------------------------

export const CONFIRMATION_KEYS = {
  CONFIRMED: "confirmed",
  DECLINED: "declined",
  PENDING: "pending",
  NO_SMS: "noSms",
};

export const getConfirmationState = (appointment) => {
  if (
    appointment.whatsappInitialDeclined === true ||
    appointment.whatsappReminderDeclined === true
  ) {
    return { key: CONFIRMATION_KEYS.DECLINED, label: "לא אושר" };
  }
  if (
    appointment.whatsappInitialConfirmed === true ||
    appointment.whatsappReminderConfirmed === true
  ) {
    return { key: CONFIRMATION_KEYS.CONFIRMED, label: "אושר" };
  }
  if (
    appointment.initialSmsEnabled === false &&
    appointment.reminderSmsEnabled === false
  ) {
    return { key: CONFIRMATION_KEYS.NO_SMS, label: "ללא SMS" };
  }
  if (
    !appointment.whatsappInitialSent &&
    !appointment.whatsappReminderSent &&
    !appointment.initialSmsScheduledAt &&
    !appointment.reminderSmsScheduledAt
  ) {
    return { key: CONFIRMATION_KEYS.NO_SMS, label: "ללא SMS" };
  }
  return { key: CONFIRMATION_KEYS.PENDING, label: "ממתין" };
};

/**
 * May the therapist trigger a manual "שלח תזכורת עכשיו" for this appointment?
 * Mirrors the server rule in functions/manualReminderHelper.js
 * (`manualReminderIneligibleReason`): a future, still-scheduled, non-blocked
 * meeting whose patient has NOT confirmed yet. Declined patients remain
 * eligible (product decision) — the sent link lets them reverse the decline.
 * The phone check is server-side (the calendar list has no patient phone).
 */
export const canSendManualReminder = (appointment, now = Date.now()) => {
  if (!appointment) return false;
  if (appointment.isDeleted === true) return false;
  if (appointment.isBlockedTime === true) return false;
  if (appointment.status !== "scheduled") return false;
  if (!appointment.startTime) return false;
  const startMs = new Date(appointment.startTime).getTime();
  if (isNaN(startMs) || startMs <= now) return false;
  if (
    appointment.whatsappInitialConfirmed === true ||
    appointment.whatsappReminderConfirmed === true
  ) {
    return false;
  }
  return true;
};

// Toolbar chips — "הכל" first, selected by default.
export const CONFIRMATION_FILTERS = [
  { key: "all", label: "הכל" },
  { key: CONFIRMATION_KEYS.CONFIRMED, label: "אושרו" },
  { key: CONFIRMATION_KEYS.DECLINED, label: "לא אושרו" },
  { key: CONFIRMATION_KEYS.PENDING, label: "ממתינים" },
];

// Blocked time and cancelled meetings are neither counted nor dimmed.
export const isCountableForConfirmation = (appointment) =>
  appointment?.isBlockedTime !== true && appointment?.status !== "cancelled";

// "ללא SMS" is treated as confirmed — same as status mode paints it turquoise.
const chipBucket = (appointment) => {
  const { key } = getConfirmationState(appointment);
  return key === CONFIRMATION_KEYS.NO_SMS ? CONFIRMATION_KEYS.CONFIRMED : key;
};

export const matchesConfirmationFilter = (appointment, filter) => {
  if (!filter || filter === "all") return true;
  if (!isCountableForConfirmation(appointment)) return true;
  return chipBucket(appointment) === filter;
};

export const countConfirmationStates = (appointments = []) => {
  const counts = { all: 0, confirmed: 0, declined: 0, pending: 0 };
  for (const apt of appointments) {
    if (!isCountableForConfirmation(apt)) continue;
    counts.all += 1;
    const bucket = chipBucket(apt);
    if (counts[bucket] !== undefined) counts[bucket] += 1;
  }
  return counts;
};

// ---------------------------------------------------------------------------
// Color helpers
// ---------------------------------------------------------------------------

const parseHex = (hex) => {
  if (typeof hex !== "string") return null;
  let h = hex.trim().replace(/^#/, "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  const n = parseInt(h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};

export const hexToRgba = (hex, alpha = 1) => {
  const c = parseHex(hex);
  if (!c) return null;
  return `rgba(${c.r}, ${c.g}, ${c.b}, ${alpha})`;
};

// Darken for text-on-tint legibility (light palette entries like lime/orange).
export const darkenHex = (hex, factor = 0.72) => {
  const c = parseHex(hex);
  if (!c) return hex;
  const d = (v) => Math.round(v * factor);
  return `rgb(${d(c.r)}, ${d(c.g)}, ${d(c.b)})`;
};

export const buildTreatmentTypeMap = (categories = []) =>
  Object.fromEntries(
    categories.map((c) => [
      c.id,
      { color: c.color || TYPE_FALLBACK_COLOR, name: c.name || "" },
    ])
  );

/**
 * @returns {{kind:'status', statusColorKey:string} |
 *           {kind:'type', hex:string, fallback:boolean, typeName:string|null}}
 */
export const resolveAppointmentColor = ({ appointment, mode, treatmentTypeMap }) => {
  if (
    mode !== CALENDAR_COLOR_MODES.TREATMENT_TYPE ||
    appointment.isBlockedTime === true ||
    appointment.status === "cancelled"
  ) {
    return {
      kind: "status",
      statusColorKey: getAppointmentStatusInfo(appointment).color,
    };
  }
  const t = treatmentTypeMap?.[appointment.treatmentTypeId];
  return {
    kind: "type",
    hex: t?.color || TYPE_FALLBACK_COLOR,
    fallback: !t,
    typeName: t?.name || null,
  };
};

// ---------------------------------------------------------------------------
// Hook — call once per VIEW (Day/Week/Month/overlay), never per card.
// RTK Query dedupes identical args, so views share one subscription.
// ---------------------------------------------------------------------------

const EMPTY = [];

export const useCalendarColorMode = () => {
  const clinicProfile = useSelector(selectClinicProfile);
  const clinicId = useSelector(selectClinicId);
  const mode = getCalendarColorMode(clinicProfile);
  const isTypeMode = mode === CALENDAR_COLOR_MODES.TREATMENT_TYPE;

  const { data: treatmentTypes = EMPTY } = useGetCategoriesQuery(
    { clinicId, type: "treatmentType" },
    { skip: !clinicId || !isTypeMode }
  );

  const treatmentTypeMap = useMemo(
    () => (isTypeMode ? buildTreatmentTypeMap(treatmentTypes) : null),
    [isTypeMode, treatmentTypes]
  );

  return { mode, treatmentTypeMap, treatmentTypes: isTypeMode ? treatmentTypes : EMPTY };
};
